/**
 * Modern Material Enterprise — app-wide tokens + MUI theme.
 * Source of truth: DesignTest/DESIGN.md
 * Type: Plus Jakarta Sans (UI/UX Pro Max — Friendly SaaS / Enterprise SaaS)
 */
import { createTheme } from "@mui/material/styles";
import { APP_FONT_FAMILY } from "../constants/typography";

const SHARED = {
  fontDisplay: APP_FONT_FAMILY,
  fontBody: APP_FONT_FAMILY,
  radiusSm: "8px",
  radiusMd: "12px",
  radiusLg: "16px",
  radiusFull: "9999px",
  elev1: "0px 1px 2px 0px rgba(28, 27, 31, 0.12), 0px 1px 3px 1px rgba(28, 27, 31, 0.08)",
  elev2: "0px 1px 2px 0px rgba(28, 27, 31, 0.15), 0px 2px 6px 2px rgba(28, 27, 31, 0.08)",
  elev4: "0px 2px 3px 0px rgba(28, 27, 31, 0.20), 0px 6px 10px 4px rgba(28, 27, 31, 0.10)",
  elev1Dark: "0px 1px 2px 0px rgba(0, 0, 0, 0.40), 0px 1px 3px 1px rgba(0, 0, 0, 0.30)",
  elev2Dark: "0px 1px 2px 0px rgba(0, 0, 0, 0.45), 0px 2px 6px 2px rgba(0, 0, 0, 0.35)",
  elev4Dark: "0px 2px 3px 0px rgba(0, 0, 0, 0.55), 0px 6px 10px 4px rgba(0, 0, 0, 0.40)",
};

/** Light tokens from DesignTest/DESIGN.md */
export const MM_LIGHT = {
  ...SHARED,
  primary: "#1C1B1F",
  primaryDark: "#000000",
  primaryContainer: "#E8E8E8",
  onPrimary: "#FFFFFF",
  onPrimaryContainer: "#1C1B1F",
  secondary: "#006A6A",
  onSecondary: "#FFFFFF",
  secondaryContainer: "#9DEEED",
  onSecondaryContainer: "#0B6E6E",
  tertiary: "#7D2A30",
  tertiaryContainer: "#9C4146",
  onTertiary: "#FFFFFF",
  error: "#BA1A1A",
  onError: "#FFFFFF",
  errorContainer: "#FFDAD6",
  background: "#F8F8F8",
  surface: "#F8F8F8",
  surfaceLowest: "#FFFFFF",
  surfaceLow: "#EEEEEE",
  surfaceContainer: "#E8E8E8",
  surfaceHigh: "#E0E0E0",
  surfaceHighest: "#D6D6D6",
  onSurface: "#1C1B1F",
  onSurfaceVariant: "#494551",
  outline: "#7A7582",
  outlineVariant: "#CBC4D2",
  hoverWash: "rgba(28, 27, 31, 0.08)",
  hoverSubtle: "rgba(28, 27, 31, 0.04)",
};

/** Dark twin — same roles inverted for dark canvases */
export const MM_DARK = {
  ...SHARED,
  elev1: SHARED.elev1Dark,
  elev2: SHARED.elev2Dark,
  elev4: SHARED.elev4Dark,
  primary: "#E8E8E8",
  primaryDark: "#FFFFFF",
  primaryContainer: "#3A383E",
  onPrimary: "#1C1B1F",
  onPrimaryContainer: "#E8E8E8",
  secondary: "#84D4D3",
  onSecondary: "#003738",
  secondaryContainer: "#004F4F",
  onSecondaryContainer: "#9DEEED",
  tertiary: "#FFB3B3",
  tertiaryContainer: "#9C4146",
  onTertiary: "#1C1B1F",
  error: "#FFB4AB",
  onError: "#690005",
  errorContainer: "#93000A",
  background: "#1C1B1F",
  surface: "#1C1B1F",
  surfaceLowest: "#2B2930",
  surfaceLow: "#323036",
  surfaceContainer: "#3A383E",
  surfaceHigh: "#444248",
  surfaceHighest: "#4E4C52",
  onSurface: "#E8E6EB",
  onSurfaceVariant: "#CAC4D0",
  outline: "#948F99",
  outlineVariant: "#49454F",
  hoverWash: "rgba(232, 232, 232, 0.10)",
  hoverSubtle: "rgba(232, 232, 232, 0.06)",
};

/** @deprecated prefer getMm(isDarkMode); kept for light-only call sites during migration */
export const MM = MM_LIGHT;

export function getMm(isDarkMode) {
  return isDarkMode ? MM_DARK : MM_LIGHT;
}

export function ensureModernMaterialFonts() {
  if (typeof document === "undefined") return;
  const id = "mm-enterprise-fonts";
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap";
  document.head.appendChild(link);
}

function buildComponentOverrides(mm, isDarkMode) {
  return {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontFamily: mm.fontBody,
        },
        body: {
          backgroundColor: mm.background,
          color: mm.onSurface,
          fontFamily: mm.fontBody,
        },
        "#root": {
          fontFamily: mm.fontBody,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: mm.radiusFull,
          minHeight: 40,
          fontWeight: 500,
          fontFamily: mm.fontBody,
          boxShadow: "none",
          textTransform: "none",
          letterSpacing: "0.1px",
          "&:hover": { boxShadow: mm.elev1 },
        },
        containedPrimary: {
          backgroundColor: mm.primary,
          color: mm.onPrimary,
          "&:hover": { backgroundColor: mm.primaryDark },
        },
        containedError: {
          backgroundColor: isDarkMode ? "#FFB4AB" : mm.error,
          color: isDarkMode ? "#690005" : mm.onError,
          "&:hover": { backgroundColor: isDarkMode ? "#FFDAD6" : "#93000A" },
        },
        outlined: {
          borderColor: mm.outline,
          color: mm.primary,
          "&:hover": {
            borderColor: mm.outline,
            backgroundColor: mm.hoverWash,
          },
        },
        text: {
          color: mm.primary,
          "&:hover": { backgroundColor: mm.hoverWash },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: mm.radiusFull,
          height: 32,
          fontWeight: 500,
          fontFamily: mm.fontBody,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: mm.surfaceLowest,
          fontFamily: mm.fontBody,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: mm.radiusMd,
          border: `1px solid ${mm.outlineVariant}`,
          boxShadow: "none",
          backgroundColor: mm.surfaceLowest,
          fontFamily: mm.fontBody,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: mm.radiusSm,
          backgroundColor: mm.surfaceLowest,
          fontFamily: mm.fontBody,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: mm.outline,
            borderWidth: "1px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: mm.outline,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: mm.primary,
            borderWidth: "2px",
          },
        },
        input: { color: mm.onSurface, fontFamily: mm.fontBody },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          fontFamily: mm.fontBody,
          "& .MuiOutlinedInput-root": {
            borderRadius: mm.radiusSm,
            fontFamily: mm.fontBody,
          },
          "& .MuiInputLabel-root": { fontFamily: mm.fontBody },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: mm.onSurfaceVariant,
          fontFamily: mm.fontBody,
          "&.Mui-focused": { color: mm.primary },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: { fontFamily: mm.fontBody },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { fontFamily: mm.fontBody },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: { fontFamily: mm.fontBody },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { fontFamily: mm.fontBody },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: mm.surfaceLow,
          fontFamily: mm.fontBody,
          "& .MuiTableCell-head": {
            color: mm.onSurfaceVariant,
            fontWeight: 500,
            fontSize: "12px",
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            borderBottom: `1px solid ${mm.outlineVariant}`,
            fontFamily: mm.fontBody,
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          "& .MuiTableRow-root:hover": {
            backgroundColor: mm.hoverSubtle,
          },
          "& .MuiTableCell-body": {
            color: mm.onSurface,
            borderBottom: `1px solid ${mm.outlineVariant}`,
            fontFamily: mm.fontBody,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: mm.radiusLg,
          boxShadow: mm.elev4,
          border: "none",
          backgroundColor: mm.surfaceLowest,
          fontFamily: mm.fontBody,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: mm.fontBody,
          fontWeight: 500,
          fontSize: "22px",
          color: mm.onSurface,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { fontFamily: mm.fontBody },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { fontFamily: mm.fontBody },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: mm.onSurfaceVariant,
          borderRadius: mm.radiusFull,
          "&:hover": { backgroundColor: mm.hoverWash },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: mm.radiusMd },
      },
    },
  };
}

export function createAppTheme(isDarkMode = false) {
  const mm = getMm(isDarkMode);
  return createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
        fhd: 1920,
      },
    },
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: mm.primary,
        dark: mm.primaryDark,
        light: mm.primaryContainer,
        contrastText: mm.onPrimary,
      },
      secondary: {
        main: mm.secondary,
        contrastText: mm.onSecondary,
        light: mm.secondaryContainer,
      },
      error: {
        main: isDarkMode ? "#FFB4AB" : mm.error,
        contrastText: isDarkMode ? "#690005" : mm.onError,
      },
      background: {
        default: mm.background,
        paper: mm.surfaceLowest,
      },
      text: {
        primary: mm.onSurface,
        secondary: mm.onSurfaceVariant,
      },
      divider: mm.outlineVariant,
      action: {
        hover: mm.hoverSubtle,
        selected: mm.secondaryContainer,
      },
    },
    typography: {
      fontFamily: mm.fontBody,
      h1: { fontFamily: mm.fontDisplay, fontWeight: 700, letterSpacing: "-0.02em" },
      h2: { fontFamily: mm.fontDisplay, fontWeight: 700, letterSpacing: "-0.02em" },
      h3: { fontFamily: mm.fontDisplay, fontWeight: 600, letterSpacing: "-0.02em" },
      h4: { fontFamily: mm.fontDisplay, fontWeight: 600, letterSpacing: "-0.015em" },
      h5: { fontFamily: mm.fontDisplay, fontWeight: 600, letterSpacing: "-0.015em" },
      h6: { fontFamily: mm.fontBody, fontWeight: 500 },
      button: {
        fontFamily: mm.fontBody,
        fontWeight: 500,
        letterSpacing: "0.1px",
        textTransform: "none",
      },
      body1: { fontFamily: mm.fontBody },
      body2: { fontFamily: mm.fontBody },
      caption: { fontFamily: mm.fontBody, color: mm.onSurfaceVariant },
    },
    shape: { borderRadius: 12 },
    shadows: [
      "none",
      mm.elev1,
      mm.elev2,
      mm.elev2,
      mm.elev4,
      ...Array(20).fill(mm.elev4),
    ],
    components: buildComponentOverrides(mm, isDarkMode),
  });
}

/** @deprecated use createAppTheme */
export function createModernMaterialTheme() {
  return createAppTheme(false);
}

export function getModernMaterialPageSx(isDarkMode = false) {
  const mm = getMm(isDarkMode);
  return {
    page: {
      backgroundColor: mm.background,
      color: mm.onSurface,
      fontFamily: mm.fontBody,
      borderRadius: mm.radiusLg,
      minHeight: "60vh",
      p: { xs: 2, sm: 3 },
      pb: 4,
      width: "100%",
    },
    title: {
      fontFamily: mm.fontDisplay,
      fontSize: { xs: "28px", sm: "32px" },
      fontWeight: 700,
      lineHeight: 1.2,
      color: mm.onSurface,
      mb: 0.5,
      letterSpacing: "-0.02em",
    },
    subtitle: {
      fontFamily: mm.fontBody,
      fontSize: "14px",
      fontWeight: 400,
      color: mm.onSurfaceVariant,
      letterSpacing: "0.25px",
    },
    filterCard: {
      mb: 2,
      borderRadius: mm.radiusMd,
      border: `1px solid ${mm.outlineVariant}`,
      boxShadow: mm.elev1,
      backgroundColor: mm.surfaceLowest,
      fontFamily: mm.fontBody,
    },
    tablePaper: {
      borderRadius: mm.radiusMd,
      border: `1px solid ${mm.outlineVariant}`,
      boxShadow: mm.elev1,
      overflow: "hidden",
      backgroundColor: mm.surfaceLowest,
      fontFamily: mm.fontBody,
    },
    primaryBtn: {
      borderRadius: mm.radiusFull,
      textTransform: "none",
      fontWeight: 500,
      fontFamily: mm.fontBody,
      backgroundColor: mm.primary,
      color: mm.onPrimary,
      px: 3,
      height: 40,
      boxShadow: "none",
      "&:hover": { backgroundColor: mm.primaryDark, boxShadow: mm.elev1 },
    },
    outlineBtn: {
      borderRadius: mm.radiusFull,
      textTransform: "none",
      fontWeight: 500,
      fontFamily: mm.fontBody,
      borderColor: mm.outline,
      color: mm.primary,
      px: 3,
      height: 40,
      "&:hover": {
        borderColor: mm.outline,
        backgroundColor: mm.hoverWash,
      },
    },
    destructiveBtn: {
      borderRadius: mm.radiusFull,
      textTransform: "none",
      fontWeight: 500,
      fontFamily: mm.fontBody,
      backgroundColor: mm.error,
      color: mm.onError,
      "&:hover": { backgroundColor: isDarkMode ? "#FFDAD6" : "#93000A" },
    },
    iconBtn: {
      color: mm.onSurfaceVariant,
      borderRadius: mm.radiusFull,
      "&:hover": { backgroundColor: mm.hoverWash, color: mm.primary },
    },
  };
}

/** Page H1 title — Plus Jakarta Sans, same scale as SettingsPage. */
export function getPageTitleSx(isDarkMode = false) {
  return getModernMaterialPageSx(isDarkMode).title;
}
